// timerStore.ts
import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { subscribeWithSelector } from "zustand/middleware";

export interface Timer {
	id: string;
	duration: number;
	elapsedTime: number;
	currentTime: number;
	remainingTime: number;
	status: "RUNNING" | "PAUSED" | "STOPPED";
	timerId: number | null;
}

export interface MultiTimerStore {
	timers: Record<string, Timer>;

	createTimer: (id: string, duration: number) => void;
	getTimer: (id: string) => Timer | null;
	startTimer: (id: string) => void;
	pauseTimer: (id: string) => void;
	resetTimer: (id: string) => void;
	removeTimer: (id: string) => void;
	setDuration: (id: string, duration: number) => void;
	setElapsedTime: (id: string, elapsedTime: number) => void;
}

export const timerStore = createStore<MultiTimerStore>()(
	subscribeWithSelector(
		immer((set, get) => ({
			timers: {},

			createTimer: (id, duration) => {
				const timer: Timer = {
					id,
					duration,
					elapsedTime: 0,
					currentTime: 0,
					remainingTime: duration,
					status: "STOPPED",
					timerId: null,
				};
				set((state) => {
					state.timers[id] = timer;
				});
			},

			getTimer: (id) => {
				const timer = get().timers[id];
				if (!timer) return null;
				return timer;
			},

			startTimer: (id) => {
				const timer = get().timers[id];
				if (!timer || timer.timerId !== null) return;

				const intervalId = window.setInterval(() => {
					const t = get().timers[id];
					if (!t) return;

					const newElapsed = t.elapsedTime + 1;
					const newRemaining = Math.max(t.duration - newElapsed, 0);

					set((state) => {
						const updated = state.timers[id];
						if (!updated) return;
						updated.elapsedTime = newElapsed;
						updated.currentTime = newElapsed;
						updated.remainingTime = newRemaining;

						if (newRemaining <= 0) {
							// Don't call pauseTimer here, as it will create a second event to the subscriber
							// instead, we will stop the timer and clear the interval
							updated.status = "STOPPED";
							updated.timerId = null;
							clearInterval(intervalId);
						}
					});
				}, 1000);

				set((state) => {
					const t = state.timers[id];
					if (!t) return;
					t.status = "RUNNING";
					t.timerId = intervalId;
				});
			},

			pauseTimer: (id) => {
				const timer = get().timers[id];
				if (!timer || timer.timerId === null) return;

				clearInterval(timer.timerId);
				set((state) => {
					const t = state.timers[id];
					if (!t) return;
					t.timerId = null;
					t.status = "PAUSED";
				});
			},

			resetTimer: (id) => {
				get().pauseTimer(id);

				set((state) => {
					const t = state.timers[id];
					if (!t) return;
					t.elapsedTime = 0;
					t.currentTime = 0;
					t.remainingTime = t.duration;
					t.status = "STOPPED";
				});
			},

			removeTimer: (id) => {
				get().pauseTimer(id);
				set((state) => {
					delete state.timers[id];
				});
			},

			setDuration: (id, duration) => {
				set((state) => {
					const t = state.timers[id];
					if (!t) return;
					t.duration = duration;
					t.remainingTime = Math.max(duration - t.elapsedTime, 0);
				});
			},

			setElapsedTime: (id, elapsedTime) => {
				set((state) => {
					const t = state.timers[id];
					if (!t) return;
					t.elapsedTime = elapsedTime;
					t.currentTime = elapsedTime;
					t.remainingTime = Math.max(t.duration - elapsedTime, 0);
				});
			},
		})),
	),
);

export type TimerStore = typeof timerStore;
